// @flow
import omit from 'lodash/omit';
import type { AddressType, Customer, Gender, CustomerPreferenceCharacteristicsType } from 'digicel-kaizen-library';
import type { Identification } from '../SignUp/Identifications/reducer';

type PersonalInfo = Customer & {
  identification: Identification,
};

export type CustomerOptionCommercialType = {
  optionInternalCommercial: boolean,
  optionExternalCommercial: boolean,
};

export type CustomerActionType =
  | {|
      type: 'SIGNUP_STORE_PASSPORT_INFO',
      payload: {
        customer: $Shape<Customer>,
        identification: Identification,
      },
    |}
  | {|
      type: 'SIGNUP_SET_PERSONAL_INFO',
      payload: PersonalInfo,
    |}
  | {|
      type: 'SIGNUP_SET_BILLING_ADDRESS',
      payload: {|
        billingAddress: AddressType,
      |},
    |}
  | {|
      type: 'SIGNUP_CONFIRM_PERSONAL_INFO',
      payload: {|
        gender: Gender,
        fullName: string,
        middleName: ?string,
        birthdate: string,
        street1: string,
        street2?: string,
        stateOrProvince: string,
        postCode: string,
        city: string,
      |},
      meta: {
        useAddressAsBilling: boolean,
        isActivatingSIM: boolean,
      },
    |}
  | {|
      type: 'GET_CUSTOMER_OFFER',
    |}
  | {|
      type: 'FETCH_CUSTOMER',
    |}
  | {|
      type: 'SET_CUSTOMER',
      payload: { customer: Object },
    |}
  | {|
      type: 'FETCH_CUSTOMER_FAILURE',
      error: any,
    |}
  | {|
      type: 'SIGNUP_CLEAR_PERSONAL_INFO',
    |}
  | {|
      type: 'HAS_MANUALLY_EDITED_ID',
    |}
  | {|
      type: 'CONFIRM_CUSTOMER_OPTION_COMMERCIAL',
      payload: {
        customerOptionCommercial: CustomerOptionCommercialType,
      },
    |}
  | {|
      type: 'SET_CUSTOMER_OPTION_COMMERCIAL',
      payload: {
        customerOptionCommercial: CustomerPreferenceCharacteristicsType,
      },
    |};

export const storePassportInfo = (passportInfo: {
  customer: $Shape<Customer>,
  identification: Identification,
}): CustomerActionType => ({
  type: 'SIGNUP_STORE_PASSPORT_INFO',
  payload: passportInfo,
});

export const setPersonalInfo = (formValues: PersonalInfo): CustomerActionType => ({
  type: 'SIGNUP_SET_PERSONAL_INFO',
  payload: formValues,
});

export const confirmPersonalInfo = (
  formValues: {|
    gender: Gender,
    fullName: string,
    birthdate: string,
    street1: string,
    street2?: string,
    stateOrProvince: string,
    postCode: string,
    city: string,
    useAddressAsBilling: boolean,
  |},
  isActivatingSIM: boolean = false
): CustomerActionType => ({
  type: 'SIGNUP_CONFIRM_PERSONAL_INFO',
  payload: omit(formValues, 'useAddressAsBilling'),
  meta: {
    useAddressAsBilling: formValues.useAddressAsBilling,
    isActivatingSIM,
  },
});

export const hasEditedIdentificationInfo = (): CustomerActionType => ({
  type: 'HAS_MANUALLY_EDITED_ID',
});

export const fetchCustomer = (): CustomerActionType => ({
  type: 'FETCH_CUSTOMER',
});

export const setCustomer = (customer: any): CustomerActionType => ({
  type: 'SET_CUSTOMER',
  payload: { customer },
});

export const fetchCustomerFailure = (error: any): CustomerActionType => ({
  type: 'FETCH_CUSTOMER_FAILURE',
  error,
});

export const confirmCustomerOptionCommercial = (
  customerOptionCommercial: CustomerOptionCommercialType
): CustomerActionType => ({
  type: 'CONFIRM_CUSTOMER_OPTION_COMMERCIAL',
  payload: {
    customerOptionCommercial,
  },
});

export const setCustomerOptionCommercial = (
  customerOptionCommercial: CustomerPreferenceCharacteristicsType
): CustomerActionType => ({
  type: 'SET_CUSTOMER_OPTION_COMMERCIAL',
  payload: {
    customerOptionCommercial,
  },
});

export const getCustomerOffer = (): CustomerActionType => ({
  type: 'GET_CUSTOMER_OFFER',
});

export const setBillingAddress = (billingAddress: AddressType): CustomerActionType => ({
  type: 'SIGNUP_SET_BILLING_ADDRESS',
  payload: {
    billingAddress,
  },
});

export const clearPersonalInfo = (): CustomerActionType => ({
  type: 'SIGNUP_CLEAR_PERSONAL_INFO',
});
